import PropTypes from 'prop-types';
import React, { Component, createRef, Fragment } from 'react';
import { DebounceInput } from 'react-debounce-input';
import Highlighter from 'react-highlight-words';
import injectSheet from 'react-jss';

import { validateBooleanQuery } from '~/api/search';
import { SEARCH_DEBOUNCE_MS } from '~/common/constants/debounces';
import { ENTER } from '~/common/constants/keyCodes';
import { Button } from '~/eds/src/components/Button/Button';
import { injectIntl } from 'react-intl';

import { black4 } from '../../../assets/shared-styles/general';
import { documentTypeIcon } from '../../DocumentsPage/Document.utils';
import EcBooleanTextSearchPopup from '../../Shared/EcBooleanTextSearchPopup/EcBooleanTextSearchPopup';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import EcMultipleLocationsLink from '../../Shared/EcMultipleLocationsLink/EcMultipleLocationsLink';
import AnalyzerResultsEmptyStateIcon from '../../Shared/Icons/AnalyzerResultsEmptyStateIcon';
import ChevronRightIcon from '../../Shared/Icons/ChevronRightIcon';
import EnterIcon from '../../Shared/Icons/EnterIcon';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import SearchIcon from '../../Shared/Icons/SearchIcon';
import { getRecentlyViewed, searchDocuments } from './AutocompleteModal.data';
import styles from './AutocompleteModal.styles';

const MIN_QUERY_LENGTH = 3;

class AutocompleteModal extends Component {
  constructor(props) {
    super(props);

    this.handleOnInputChange = this.handleOnInputChange.bind(this);
    this.handleOnInputKeyDown = this.handleOnInputKeyDown.bind(this);
    this.handleBooleanPopupOpen = this.handleBooleanPopupOpen.bind(this);
    this.handleBooleanPopupClose = this.handleBooleanPopupClose.bind(this);
    this.handleOnDocumentClick = this.handleOnDocumentClick.bind(this);

    this.state = {
      searchQuery: '',
      loading: false,
      loadingError: false,
      validationError: null,
      validationWarning: null,
      defaultResults: [],
      results: [],
      remainingResults: 0,
      totalResultsCount: 0,
      isSyntaxPopupOpen: false,
    };
  }

  helpButton = createRef();

  componentDidMount() {
    const { forGroupTreeView } = this.props;
    // getRecentlyViewed does not have document ID for add to group to work
    if (!forGroupTreeView) {
      this.setState({ loading: true });

      getRecentlyViewed().then((res) => {
        const recentlyViewedList = res.results.map((item) => ({
          fileType: item.file_type,
          label: item.document_name,
          id: item.document_handler_id,
          path: item.path,
        }));
        this.setState({ loading: false, defaultResults: recentlyViewedList });
      });
    }
  }

  handleBooleanPopupOpen() {
    this.setState({ isSyntaxPopupOpen: true });
  }

  handleBooleanPopupClose() {
    this.setState({ isSyntaxPopupOpen: false });
    this.helpButton.current.focus();
  }

  handleOnInputChange(event) {
    const { intl } = this.props;
    const query = event.target.value;
    this.setState({ searchQuery: query });

    if (query.length >= MIN_QUERY_LENGTH) {
      validateBooleanQuery(query)
        .then((res) => {
          this.setState({
            loading: true,
            loadingError: false,
            validationError: null,
            validationWarning: res.detail,
          });

          const requestQuery = [{ entity: 'bool_text_search', query }];
          searchDocuments(requestQuery)
            .then((res) => {
              const results = res.results.map((document) => ({
                label: document.document_handlers[0].document_name,
                documentId: document.id,
                document_handlers: document.document_handlers,
              }));
              const remainingResults = Math.max(0, res.count - results.length);

              this.setState({ loading: false, results, remainingResults });
              this.setState({
                loading: false,
                results,
                remainingResults,
                totalResultsCount: res.count,
              });
            })
            .catch(() =>
              this.setState({
                loading: false,
                loadingError: true,
                validationError: intl.formatMessage({
                  id: 'AutocompleteModal.searchError',
                  defaultMessage: 'Your query is invalid.',
                  description: 'Error message for invalid search query',
                }),
              }),
            );
        })
        .catch((err) =>
          this.setState({
            loading: false,
            validationError: err?.response?.data?.detail,
          }),
        );
    }
  }

  handleOnInputKeyDown(event) {
    const { handleOnShowMoreClick } = this.props;
    const { searchQuery, validationError } = this.state;

    if (
      event.keyCode === ENTER &&
      searchQuery &&
      searchQuery.length >= MIN_QUERY_LENGTH &&
      !validationError
    ) {
      handleOnShowMoreClick(searchQuery);
    }
  }

  handleOnDocumentClick(id) {
    const { handleOnSearchItemClick, hideModal } = this.props;

    hideModal();
    handleOnSearchItemClick(id);
  }

  renderDocumentPath(path) {
    return path.map((folder, folderIndex) => (
      <Fragment key={`${folder.id}-${folderIndex}`}>
        <div>{folder.name}</div>
        {path.length - 1 !== folderIndex && (
          <ChevronRightIcon size="16" color={black4} />
        )}
      </Fragment>
    ));
  }

  renderDefaultResultsContainer() {
    const { classes, intl } = this.props;
    const { defaultResults, loading } = this.state;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <LoadingSpinner size="medium" />
        </div>
      );
    }
    return defaultResults.length ? (
      <div className={classes.resultsList}>
        <div className={classes.resultsListLabel}>
          {intl.formatMessage({
            id: 'AutocompleteModal.recentlyViewedLabel',
            defaultMessage: 'RECENTLY VIEWED DOCUMENTS',
            description: 'Label for recently viewed documents section',
          })}
        </div>
        {defaultResults.map((item, index) => (
          <button
            className={classes.listItem}
            key={index}
            onClick={() => this.handleOnDocumentClick(item.id)}
          >
            <span aria-hidden={true}>{documentTypeIcon(item.fileType)}</span>
            <div className={classes.listItemContent}>
              <div className={classes.listItemName}>{item.label}</div>
              <div className={classes.listItemPath}>
                {this.renderDocumentPath(item.path)}
              </div>
            </div>
          </button>
        ))}
      </div>
    ) : null;
  }

  renderEmptyResultsList() {
    const { classes, intl } = this.props;

    return (
      <div className={classes.emptyResultsList}>
        <AnalyzerResultsEmptyStateIcon />
        <div className={classes.emptyResultsListTitle}>
          {intl.formatMessage({
            id: 'AutocompleteModal.noDocumentsFound',
            defaultMessage: 'No documents found.',
            description: 'Empty search results title',
          })}
        </div>
        <div className={classes.emptyResultsListSubtitle}>
          {intl.formatMessage({
            id: 'AutocompleteModal.trySearchingElse',
            defaultMessage: 'Try searching for something else.',
            description: 'Empty search results subtitle',
          })}
        </div>
      </div>
    );
  }

  renderResultsList() {
    const {
      classes,
      handleOnShowMoreClick,
      toViewDocument,
      handleOnSearchItemClick,
      intl,
    } = this.props;
    const { searchQuery, results, remainingResults } = this.state;

    const renderedSearchList = results.map((item, index) => {
      if (toViewDocument) {
        return (
          <Fragment key={index}>
            <EcMultipleLocationsLink
              documentName={item.label}
              documentHandlers={item.document_handlers}
              wrapperStyles={classes.listItem}
              multipleLocationsCallback={() => this.props.hideModal()}
            >
              <span aria-hidden={true}>
                {documentTypeIcon(item.document_handlers[0].file_type)}
              </span>
              <Highlighter
                highlightClassName="highlight"
                searchWords={[searchQuery]}
                autoEscape={true}
                textToHighlight={item.label}
              />
            </EcMultipleLocationsLink>
          </Fragment>
        );
      } else {
        return (
          <div className={classes.listItem} key={index}>
            {documentTypeIcon(item.document_handlers[0].file_type)}

            <Button
              text={item.label}
              onClick={() => handleOnSearchItemClick(item.documentId)}
            />
          </div>
        );
      }
    });

    return (
      <div className={classes.resultsList}>
        <div className={classes.resultsListLabel}>
          {intl.formatMessage({
            id: 'AutocompleteModal.documentsLabel',
            defaultMessage: 'documents',
            description: 'Documents section label',
          })}
        </div>
        {renderedSearchList}
        {remainingResults > 0 && (
          <Button
            variant="action"
            onClick={() => handleOnShowMoreClick(searchQuery)}
            icon="plus"
            iconPosition="left"
            text={intl.formatMessage(
              {
                id: 'AutocompleteModal.moreDocuments',
                defaultMessage:
                  '{count} more {count, plural, one {document} other {documents}}',
                description:
                  'Show more documents button text. Example: count = "25"',
              },
              {
                count: remainingResults,
              },
            )}
          />
        )}
      </div>
    );
  }

  renderResultsListContainer() {
    const { results } = this.state;

    return results.length
      ? this.renderResultsList()
      : this.renderEmptyResultsList();
  }

  renderResultsContainer() {
    const { classes, intl } = this.props;
    const { loading, validationError, loadingError, validationWarning } =
      this.state;

    return loading ? (
      <div className={classes.loadingContainer}>
        <LoadingSpinner size="medium" />
      </div>
    ) : validationError && !loadingError ? (
      <div className={classes.errorMessage}>{validationError}</div>
    ) : loadingError ? (
      <div className={classes.errorMessage}>
        {intl.formatMessage({
          id: 'AutocompleteModal.searchResultsError',
          defaultMessage: 'An error occurred while querying the search.',
          description: 'Search error message',
        })}
      </div>
    ) : validationWarning ? (
      <div>
        <div className={classes.boolWarningMessage}>{validationWarning}</div>
        {this.renderResultsListContainer()}
      </div>
    ) : (
      this.renderResultsListContainer()
    );
  }

  render() {
    const { classes, hideModal, toViewDocument, intl } = this.props;
    const { searchQuery, isSyntaxPopupOpen, totalResultsCount } = this.state;
    const renderTitleControls = () => (
      <div className={classes.searchActions}>
        <Button
          text={intl.formatMessage({
            id: 'AutocompleteModal.helpButton',
            defaultMessage: 'Help',
            description: 'Help button text',
          })}
          variant="action"
          onClick={this.handleBooleanPopupOpen}
        />
      </div>
    );

    return (
      <>
        <EcModalCard
          title={intl.formatMessage({
            id: 'AutocompleteModal.searchTitle',
            defaultMessage: 'Search',
            description: 'Modal title',
          })}
          renderTitleControls={renderTitleControls}
          content={
            <>
              <div className={classes.searchInputContainer}>
                <SearchIcon color="#000" opacity="0.25" />
                <DebounceInput
                  aria-describedby="quick-search-results-list"
                  autoFocus={true}
                  debounceTimeout={SEARCH_DEBOUNCE_MS}
                  type="search"
                  placeholder={intl.formatMessage({
                    id: 'AutocompleteModal.searchPlaceholder',
                    defaultMessage: 'Search for anything…',
                    description: 'Search input placeholder',
                  })}
                  className={classes.searchInput}
                  onChange={this.handleOnInputChange}
                  onKeyDown={this.handleOnInputKeyDown}
                />
                {searchQuery.length >= MIN_QUERY_LENGTH && (
                  <div className={classes.searchLabel}>
                    {intl.formatMessage({
                      id: 'AutocompleteModal.searchLabel',
                      defaultMessage: 'Search',
                      description: 'Search label',
                    })}
                    <EnterIcon />
                  </div>
                )}
                <div
                  aria-live="assertive"
                  className="screenReaderText"
                  id="quick-search-results-list"
                  role="status"
                >
                  {!searchQuery.length
                    ? null
                    : totalResultsCount
                      ? intl.formatMessage(
                          {
                            id: 'AutocompleteModal.documentsFound',
                            defaultMessage: '{count} document(s) found',
                            description:
                              'Search results count. Example: count = "15"',
                          },
                          { count: totalResultsCount },
                        )
                      : intl.formatMessage({
                          id: 'AutocompleteModal.noDocumentsFoundAria',
                          defaultMessage: 'No documents found.',
                          description: 'No documents found aria label',
                        })}
                </div>
              </div>
              {searchQuery.length ? (
                searchQuery.length < MIN_QUERY_LENGTH ? (
                  <div
                    role="alert"
                    aria-live="polite"
                    className={classes.warningMessage}
                  >
                    {intl.formatMessage(
                      {
                        id: 'AutocompleteModal.minCharactersWarning',
                        defaultMessage: 'Type at least {count} characters …',
                        description:
                          'Minimum characters warning. Example: count = "3"',
                      },
                      { count: MIN_QUERY_LENGTH },
                    )}
                  </div>
                ) : (
                  this.renderResultsContainer()
                )
              ) : (
                this.renderDefaultResultsContainer()
              )}
              {toViewDocument ? null : (
                <div className={classes.modalActions}>
                  <Button
                    text={intl.formatMessage({
                      id: 'AutocompleteModal.cancelButton',
                      defaultMessage: 'Cancel',
                      description: 'Cancel button text',
                    })}
                    onClick={hideModal}
                  />
                </div>
              )}
            </>
          }
          hideModal={hideModal}
        />
        {isSyntaxPopupOpen && (
          <EcBooleanTextSearchPopup
            relativeContained
            handleOnCloseClick={this.handleBooleanPopupClose}
          />
        )}
      </>
    );
  }
}

AutocompleteModal.propTypes = {
  classes: PropTypes.object.isRequired,
  hideModal: PropTypes.func.isRequired,
  handleOnSearchItemClick: PropTypes.func.isRequired,
  handleOnShowMoreClick: PropTypes.func.isRequired,
};

export default injectSheet(styles)(injectIntl(AutocompleteModal));
